/* OsPlanEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");
const Registrations = require("../../../RegistrationNamespaces");

/**
 * @param {UserSession} userSession
 * @param {planEntity} planEntity
 * @param {SolutionsManager} solutionsManager
 * @param {IO} steveIo
 */

class OsPlanEditor extends GameEditor {
    constructor(
        logger,
        di,
        registeredDetails,
        editorState,
        editorLayoutMgr,
        editorLayout,
        DeviceEntity,
        OsUpgradePlan,
        OsUpgradeExecutionEntity,
        OsImagePathsEntity,
        userSession,
        userService,
        editorTabCmpnt,
        solutionsManager,
        steveIo,
        OsImage,
        OsImageCatalog,
        AutoCapturePrefs
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._registeredDetails = registeredDetails;
        this._editorLayout = editorLayout;
        this._DeviceEntity = DeviceEntity;
        this._planEntity = OsUpgradePlan;
        this._executionEntity = OsUpgradeExecutionEntity;
        this._osImagePathsEntity = OsImagePathsEntity;
        this._osImageEntity = OsImage;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._planDeleted = false;
        this._steveIo = steveIo;
        this._userSession = userSession;
        this._userService = userService;
        this._activeOrgId = userSession.activeOrganization.id;
        this._pagingExecutionFeed = undefined;
        this._pagingImagesFeed = undefined;
        this._planCancelInProgress = undefined;
        this._osImageCatalog = OsImageCatalog;

        this._autoCapturePrefs = AutoCapturePrefs;
        this._autoCapturePrefsEntity = undefined;

        this._logs = {};
        this._imageNameCache = new Map();

        let solutionDefinition = solutionsManager.getActiveSolution();
        let planEditorViews = _.get(solutionDefinition, "osPlanEditor.viewNamespaces", []);
        let planEditorActions = _.get(solutionDefinition, "osPlanEditor.actionNamespaces", []);
        let launchOptions = editorState.launchOptions;
        this.setNamespaceGlobs(planEditorViews.concat(planEditorActions), true);
        this._osPlanStatusList = this._planEntity.OS_UPGRADE_STATUS;
        this._expandOpt = launchOptions.expandOpt;

        this._plan = undefined;
        this._planId = launchOptions.planId;
        this._planName = launchOptions.title || "Unknown";

        this._runWorkId = undefined;

        this.retardActions(true);

        this.whenPlanIsReady = _.memoize(this.whenPlanIsReady);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListenersOnPlan() {
        this._plan.on(
            "NamePropertyUpdated",
            (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
        );
        this._plan.on(
            "EntityDeletedFromSystem",
            (this._onPlanRemoved = this._onPlanRemoved.bind(this))
        );
        this._plan.on(
            "DirtyStateChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListenersOnPlan() {
        this._plan.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
        this._plan.removeListener("EntityDeletedFromSystem", this._onPlanRemoved);
        this._plan.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        this.updateTab(this._plan.pristineProperties.name);
    }

    _onPlanRemoved() {
        const _debouncedClose = _.bind(_.debounce(this.closeEditor, 300), this);
        _debouncedClose(this.editorTabStateId, true);
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getPlan() {
        return this._plan;
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get osImageEntity() {
        return this._osImageEntity;
    }

    get osImagePathsEntity() {
        return this._osImagePathsEntity;
    }

    get planEntity() {
        return this._planEntity;
    }

    getDeviceEntity() {
        return this._DeviceEntity;
    }

    get steveIO() {
        return this._steveIo;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    get isOsUpgradeAutoCaptured() {
        return _.get(this._autoCapturePrefsEntity.getOwnOrgEvents(), "deviceOsUpgrade", false);
    }

    get isOsTransferAutoCaptured() {
        return _.get(this._autoCapturePrefsEntity.getOwnOrgEvents(), "deviceOsTransfer", false);
    }

    whenPlanIsReady() {
        return this._autoCapturePrefs
            .loadAscendAutoCapturePrefs(this.activeOrgId, {})
            .then((autoCapturePrefsEntity) => {
                this._autoCapturePrefsEntity = autoCapturePrefsEntity;
                return Promise.resolve();
            })
            .then(() => {
                return this._planEntity.retrieveOsUpgradePlan(this.activeOrgId, this._planId);
            })
            .then((plans) => {
                this._plan = plans;
                this._registerListenersOnPlan();
                return this._plan;
            });
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    _shutdownAllFeeds() {
        const promises = [];
        Object.values(this._logs).forEach((feed) => {
            if (feed && feed.shutdown) {
                promises.push(feed.shutdown);
            }
        });

        return Promise.all(promises);
    }

    shutdown() {
        let logger = this._logger;
        this.whenPlanIsReady.cache.clear();
        return super
            .shutdown()
            .then(() => {
                if (!_.isUndefined(this._plan)) {
                    this._unregisterListenersOnPlan();
                    return this._plan.shutdown();
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => this._shutdownAllFeeds())
            .then(() => {
                return this._pagingImagesFeed && this._pagingImagesFeed.shutdown();
            })
            .then(() => {
                this._logger = undefined;
                this._editorTabState = undefined;
                this._steveIo = undefined;
                this._planDeleted = undefined;
                this._DeviceEntity = undefined;
                this._planEntity = undefined;
                this._executionEntity = undefined;

                this._tabCmpnt = undefined;
                this._plan = undefined;
                this._registeredDetails = undefined;
                this._editorLayout = undefined;
                this._activeOrgId = undefined;
                this._userSession = undefined;
                this._planId = undefined;
                this._planName = undefined;
                this._pagingExecutionFeed = undefined;
                this._pagingImagesFeed = undefined;
                this._userService = undefined;
                this._osImageCatalog = undefined;
                this._runWorkId = undefined;
                this._planCancelInProgress = undefined;

                this._osImagePathsEntity = undefined;
                this._osImageEntity = undefined;
                this._TriggerTypeEntitiy = undefined;

                this._autoCapturePrefs = undefined;
                this._autoCapturePrefsEntity = undefined;

                this._logs = undefined;
                this._imageNameCache = undefined;
                // document.dispatchEvent(new Event("ED_Shutdown"));
            })
            .catch((err) => {
                logger.warn({error: err}, "OsPlanEditor.shutdown() error");
            });
    }
    launchNewTab(planDetails) {
        let launchOpts = {
            id: planDetails.id,
            title: _.get(planDetails, "name", ""),
        };
        const planId = launchOpts.id;
        const stringToHash = planId;
        this.generateHash(stringToHash)
            .catch((error) => this._logger.warn({error}))
            .then((tabId) => {
                launchOpts = _.assign({}, launchOpts, {
                    id: tabId,
                    planId,
                });
                return this._editorLayout.launchEditor(
                    {fqn: Registrations.OS_PLAN_EDITOR_VIEW_DETAILS},
                    Registrations.OS_PLAN_EDITOR,
                    launchOpts
                );
            });
    }
    /*******************************************/
    /* END PUBLIC COMPONENT MODEL METHODS      */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /**
     * Function to get Work related to the provided Config Audit Execution
     * @param {ConfigAuditExecutionEntity} executionEntity - Config Audit Execution Entity instance which related work is needed
     * @param {string[]} [fieldsList = []] - list of fields to get
     * @returns {Promise<WorkEntity | Error>}
     */
    createPlan(details) {
        const osPlan = new this._planEntity(this._activeOrgId);
        osPlan.name = details;
        osPlan.description = "";
        osPlan.groupedBy = "detectedSku";
        osPlan.action = "TRANSFER";
        if (this.isOsTransferAutoCaptured) {
            osPlan.snapshotType = "POST";
        } else {
            osPlan.snapshotType = "NONE";
        }

        return osPlan.create();
    }

    /* jshint ignore:start */
    async moveDevices(newPlanId, deviceIds, groupKey = "") {
        try {
            await this._plan.moveDevices(deviceIds, newPlanId, groupKey);
        } catch (error) {
            return error;
        }
    }

    async moveDevicesWhenOnePlanSelected(newPlanId, deviceIds) {
        try {
            await this._removeDevices(deviceIds);
            const _newPlan = await this._planEntity.retrieveOsUpgradePlan(
                this.activeOrgId,
                newPlanId
            );
            await _newPlan.addDevicesToPlan(deviceIds);
            _newPlan.update();
        } catch (error) {
            return error;
        }
    }

    async _removeDevices(deviceIds, isGrouped = false) {
        try {
            if (isGrouped) {
                await this._plan.removeDevicesForGroupKey(deviceIds);
            } else {
                await this._plan.removeDevicesFromPlan(deviceIds);
            }
            await this._plan.updateRelationships();
        } catch (error) {
            return error;
        }
    }
    /* jshint ignore:end */

    loadDevicesForGroupKey(groupKey) {
        return this._plan.loadDevicesForGroupKey(groupKey, undefined, undefined);
    }
    _getImageNameByIds(imageIds) {
        // Memoized per id for the editor's lifetime: the grid re-creates Target Files cells on
        // every page swap, and a null entry marks a not-found id so it is never re-queried.
        const missingIds = imageIds.filter((id) => !this._imageNameCache.has(id));
        if (missingIds.length > 0) {
            let query = {id: {$in: missingIds}};
            const loadPromise = this._osImageEntity.loadAscendOsImages(
                this._activeOrgId,
                query,
                undefined,
                undefined,
                undefined
            );
            missingIds.forEach((id) => {
                const imagePromise = loadPromise
                    .then((images) => _.find(images, (img) => _.get(img, "id") === id))
                    .catch(() => undefined)
                    .then((image) => {
                        const resolved = _.isUndefined(image) ? null : image;
                        if (this._imageNameCache) {
                            this._imageNameCache.set(id, resolved);
                        }
                        return resolved;
                    });
                this._imageNameCache.set(id, imagePromise);
            });
        }
        return Promise.all(imageIds.map((id) => this._imageNameCache.get(id))).then((images) =>
            images.filter((image) => !_.isNil(image))
        );
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsPlanEditor;

OsPlanEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "$teve.registeredDetails",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "EditorLayout.EditorLayout",
    "$teve.entities.Device",
    "$teve.entities.OsUpgradePlan",
    "$teve.entities.OsUpgradeExecution",
    "$teve.entities.OsImagePaths",
    "$teve.userSession",
    "$teve.services.instance.User",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.io",
    "$teve.entities.OsImage",
    "$teve.entities.OsImageCatalog",
    "$teve.entities.AutoCapturePrefs",
];

//Optional convenient way to set a tab icon.
OsPlanEditor.$editorIcon = "osmanager.os_plan";
OsPlanEditor.$editorIconType = "base-icon-gl";
